import { supabase } from '@/integrations/supabase';
import { 
  ShowcaseItem, 
  GalleryFilters, 
  mapDbToShowcaseItem, 
  mapShowcaseItemToDb, 
  showcaseItemSchema
} from '@/types/showcase';
import { SecondMeConfig } from '@/components/second-me/types';

/**
 * Service for managing showcase items in the gallery
 */
export const showcaseService = {
  /**
   * Fetch showcase items with filtering and pagination
   */
  async getShowcaseItems(filters: GalleryFilters): Promise<{ items: ShowcaseItem[], total: number }> {
    let query = supabase
      .from('showcase_items')
      .select('*', { count: 'exact' });

    // Apply filters
    if (filters.search) {
      query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
    }

    if (filters.tags && filters.tags.length > 0) {
      query = query.contains('tags', filters.tags);
    }

    if (filters.itemType && filters.itemType.length > 0) {
      query = query.in('item_type', filters.itemType);
    }

    if (filters.featured !== undefined) {
      query = query.eq('featured', filters.featured);
    }

    if (filters.createdBy && filters.createdBy.length > 0) {
      query = query.in('created_by', filters.createdBy);
    }

    if (filters.complexity && filters.complexity.length > 0) {
      const metadataConditions = filters.complexity.map(complexity => 
        `metadata->>'complexity' = '${complexity}'`
      ).join(' OR ');
      
      query = query.or(metadataConditions);
    }

    if (filters.capabilities && filters.capabilities.length > 0) {
      // For capabilities stored in metadata JSON array
      filters.capabilities.forEach(capability => {
        query = query.contains('metadata->capabilities', [capability]);
      });
    }

    if (filters.dateRange) {
      query = query
        .gte('created_at', filters.dateRange.start.toISOString())
        .lte('created_at', filters.dateRange.end.toISOString());
    }

    // Apply sorting
    switch (filters.sortBy) {
      case 'newest':
        query = query.order('created_at', { ascending: false });
        break;
      case 'oldest':
        query = query.order('created_at', { ascending: true });
        break;
      case 'popular':
        query = query.order('likes', { ascending: false });
        break;
      case 'trending':
        // For trending, we might want a more complex algorithm, but for now we'll 
        // just use recent + popular as a simplification
        query = query.order('views', { ascending: false }).order('created_at', { ascending: false });
        break;
      case 'rating':
        query = query.order('metadata->averageRating', { ascending: false });
        break;
      default:
        query = query.order('created_at', { ascending: false });
        break;
    }

    // Apply pagination
    query = query.range(filters.offset, filters.offset + filters.limit - 1);

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching showcase items:', error);
      throw error;
    }

    return {
      items: data.map(mapDbToShowcaseItem),
      total: count || 0
    };
  },

  /**
   * Get a single showcase item by ID
   */
  async getShowcaseItem(id: string): Promise<ShowcaseItem | null> {
    const { data, error } = await supabase
      .from('showcase_items')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Item not found
      }
      console.error('Error fetching showcase item:', error);
      throw error;
    }

    return mapDbToShowcaseItem(data);
  },

  /**
   * Create a new showcase item
   */
  async createShowcaseItem(item: ShowcaseItem): Promise<ShowcaseItem> {
    const validatedItem = showcaseItemSchema.parse(item);
    const dbItem = mapShowcaseItemToDb(validatedItem);

    const { data, error } = await supabase
      .from('showcase_items')
      .insert(dbItem)
      .select()
      .single();

    if (error) {
      console.error('Error creating showcase item:', error);
      throw error;
    }

    return mapDbToShowcaseItem(data);
  },

  /**
   * Update an existing showcase item
   */
  async updateShowcaseItem(id: string, item: Partial<ShowcaseItem>): Promise<ShowcaseItem> {
    const dbItem = mapShowcaseItemToDb(item as ShowcaseItem);

    const { data, error } = await supabase
      .from('showcase_items')
      .update(dbItem)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating showcase item:', error);
      throw error;
    }

    return mapDbToShowcaseItem(data);
  },

  /**
   * Delete a showcase item
   */
  async deleteShowcaseItem(id: string): Promise<void> {
    const { error } = await supabase
      .from('showcase_items')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting showcase item:', error);
      throw error;
    }
  },

  /**
   * Get featured showcase items
   */
  async getFeaturedItems(limit: number = 6): Promise<ShowcaseItem[]> {
    const { data, error } = await supabase
      .from('showcase_items')
      .select('*')
      .eq('featured', true)
      .eq('is_public', true)
      .order('likes', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching featured items:', error);
      throw error;
    }

    return data.map(mapDbToShowcaseItem);
  },

  /**
   * Publish a Second Me configuration to the showcase
   */
  async publishAgentToShowcase(
    config: SecondMeConfig, 
    showcaseDetails: { 
      title: string; 
      description: string; 
      thumbnailUrl?: string; 
      tags: string[]; 
      isPublic: boolean;
      sharedWith?: string[];
      metadata: any;
    }
  ): Promise<ShowcaseItem> {
    // First, we need to ensure the SecondMeConfig is saved
    // This would be handled by a separate service for Second Me Config storage
    // For this implementation, we'll assume config.id exists

    if (!config.id) {
      throw new Error('Cannot publish unsaved configuration');
    }

    // Create the showcase item
    const showcaseItem: ShowcaseItem = {
      title: showcaseDetails.title,
      description: showcaseDetails.description,
      thumbnailUrl: showcaseDetails.thumbnailUrl,
      itemType: 'agent',
      contentId: config.id,
      isPublic: showcaseDetails.isPublic,
      sharedWith: showcaseDetails.sharedWith || [],
      tags: showcaseDetails.tags,
      metadata: {
        ...showcaseDetails.metadata,
        capabilities: config.expertise || [],
        complexity: showcaseDetails.metadata.complexity || 'intermediate',
      }
    };

    return this.createShowcaseItem(showcaseItem);
  },

  /**
   * Increment the view count for a showcase item
   */
  async incrementViewCount(id: string): Promise<void> {
    const { error } = await supabase.rpc('increment_showcase_view', { item_id: id });

    if (error) {
      console.error('Error incrementing view count:', error);
      throw error;
    }
  },

  /**
   * Get the user's showcase items
   */
  async getUserShowcaseItems(userId: string): Promise<ShowcaseItem[]> {
    const { data, error } = await supabase
      .from('showcase_items')
      .select('*')
      .eq('created_by', userId);

    if (error) {
      console.error('Error fetching user showcase items:', error);
      throw error;
    }

    return data.map(mapDbToShowcaseItem);
  },

  /**
   * Search showcase items
   */
  async searchShowcaseItems(searchTerm: string, limit: number = 10): Promise<ShowcaseItem[]> {
    const { data, error } = await supabase
      .from('showcase_items')
      .select('*')
      .or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,tags.cs.{${searchTerm}}`)
      .eq('is_public', true)
      .limit(limit);

    if (error) {
      console.error('Error searching showcase items:', error);
      throw error;
    }

    return data.map(mapDbToShowcaseItem);
  },

  /**
   * Get similar showcase items based on tags and metadata
   */
  async getSimilarItems(itemId: string, limit: number = 4): Promise<ShowcaseItem[]> {
    // First get the item to find its tags
    const { data: itemData, error: itemError } = await supabase
      .from('showcase_items')
      .select('*')
      .eq('id', itemId)
      .single();

    if (itemError) {
      console.error('Error fetching item for similarity search:', itemError);
      throw itemError;
    }

    const item = mapDbToShowcaseItem(itemData);
    
    // Then find items with similar tags
    const { data, error } = await supabase
      .from('showcase_items')
      .select('*')
      .neq('id', itemId) // Exclude the current item
      .eq('is_public', true)
      .eq('item_type', item.itemType)
      .contains('tags', item.tags)
      .limit(limit);

    if (error) {
      console.error('Error fetching similar items:', error);
      throw error;
    }

    return data.map(mapDbToShowcaseItem);
  }
};